import { Fragment } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Modulo } from '@eleg360/types';
import { useAuthStore } from '@/lib/stores/authStore';
import { adminApi } from '@/lib/api/admin';
import { MOD_COLORS, MODULOS, rotasDoModulo, grupoDaRota } from '@/lib/navegacao';
import { useModulo } from '@/lib/hooks/useModulo';
import { useTheme } from '@/lib/hooks/useTheme';
import { contatosApi } from '@/lib/api/contatos';
import { liderancasApi } from '@/lib/api/liderancas';
import { solicitacoesApi } from '@/lib/api/solicitacoes';
import { acoesApi } from '@/lib/api/acoes';
import { visitasApi } from '@/lib/api/visitas';
import { pesquisasApi } from '@/lib/api/pesquisas';
import { fontesApi } from '@/lib/api/oto';
import { estrategiasApi } from '@/lib/api/estrategias';
import { alertasApi, notificacoesApi } from '@/lib/api/core';
import { toast } from '@/lib/stores/toastStore';
import { LogoSVG } from '@/design-system/LogoSVG';
import { Icone } from '@/design-system/icons';

/**
 * Contador de um item do menu, sobre os dados reais.
 *
 * `staleTime` alto porque o menu fica montado o tempo todo: sem isso cada troca
 * de tela dispararia dez requisições só para atualizar números pequenos. As
 * chaves são as mesmas usadas pelas páginas, então o cache é compartilhado e o
 * número acompanha o que a tela acabou de gravar.
 */
function useContador<T>(
  chave: unknown[],
  fn: () => Promise<T>,
  extrai: (d: T) => number,
  ativo: boolean,
): number | undefined {
  return useQuery({
    queryKey: chave,
    queryFn: fn,
    enabled: ativo,
    staleTime: 60_000,
    select: extrai,
  }).data;
}

export function Sidebar() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token) || '';

  // Identidade visual do módulo ativo: o `themeStore` é a fonte única (ver
  // `AppLayout.tsx`, que usa o mesmo `mod` para o atributo `data-mod` que
  // colore o app inteiro). A sincronização rota→store é a Task 1.6.
  const { modulo: chaveModulo } = useModulo();
  const { toggleTheme } = useTheme();
  const modAtivo = chaveModulo ? MODULOS[chaveModulo] : null;

  // Módulos contratados pelo mandato: define quais rotas ficam bloqueadas
  const { data: mandato } = useQuery({
    queryKey: ['mandato'],
    queryFn: () => adminApi.getMandato(token),
    enabled: !!token,
    staleTime: 60_000,
  });
  const contratados = mandato?.modulos;
  const tenantCidadeUf = mandato ? [mandato.cidade, mandato.uf].filter(Boolean).join(' · ') : 'Fortaleza · CE';
  const periodoDoMandato =
    mandato?.mandato_inicio && mandato?.mandato_fim
      ? `Mandato ${mandato.mandato_inicio}–${mandato.mandato_fim}`
      : '';

  /**
   * Falha aberta de propósito: enquanto a lista de módulos não chegou (ou a
   * chamada falhou) nada é bloqueado. Barrar por engano esconderia o sistema
   * inteiro do usuário; deixar passar apenas devolve a recusa da API, que já é a
   * autoridade sobre o assunto.
   */
  const liberado = (m: Modulo | undefined) => !m || !contratados || contratados.includes(m);
  const logado = !!token;

  const nContatos = useContador(['contatos', { limit: 1 }], () => contatosApi.list({ limit: 1 }, token), (d) => d.total, logado && liberado('gab'));
  const nLiderancas = useContador(['liderancas', {}], () => liderancasApi.list({}, token), (d) => d.length, logado && liberado('gab'));
  const nSolicAbertas = useContador(['solicitacoes', {}], () => solicitacoesApi.list({}, token), (d) => d.filter((s) => s.status !== 'concluida').length, logado && liberado('gab'));
  const nVisitas = useContador(['visitas'], () => visitasApi.list(token), (d) => d.length, logado && liberado('gab'));
  const nAcoes = useContador(['acoes', {}], () => acoesApi.list({}, token), (d) => d.length, logado && liberado('gab'));
  const nNaoLidas = useContador(['notificacoes', false], () => notificacoesApi.list(token), (d) => d.filter((n) => !n.lida).length, logado);
  const nPesquisas = useContador(['pesquisas'], () => pesquisasApi.list(token), (d) => d.length, logado && liberado('eleg'));
  // Não lidos, não o total — simétrico a `nNaoLidas` acima. O contador do
  // menu existe para dizer o que falta olhar; contando tudo, ele nunca
  // baixava e o botão "Lido" da tela de alertas não mudava nada no menu.
  const nAlertas = useContador(['alertas'], () => alertasApi.list(token), (d) => d.filter((a) => !a.lida).length, logado && liberado('eleg'));
  const nFontes = useContador(['fontes', {}], () => fontesApi.list(token), (d) => d.length, logado && liberado('oto'));
  const nEstrategias = useContador(['estrategias'], () => estrategiasApi.list(token), (d) => d.length, logado && liberado('oto'));

  // Contador de cada item do menu, indexado pelo caminho — os mesmos
  // `useContador` de antes (mesma chave, mesma condição de habilitação),
  // só que consultados a partir da rota em vez de um `navGroups` fixo.
  const contadores: Record<string, number | undefined> = {
    '/notificacoes': nNaoLidas,
    '/contatos': nContatos,
    '/liderancas': nLiderancas,
    '/solicitacoes': nSolicAbertas,
    '/visitas': nVisitas,
    '/acoes': nAcoes,
    '/fontes': nFontes,
    '/estrategias': nEstrategias,
    '/pesquisas': nPesquisas,
    '/alertas': nAlertas,
  };

  // O menu é filtrado pelo módulo ativo — é `rotasDoModulo` (Task 1.4) que
  // reproduz o filtro `renderNav` do protótipo (proto:1885-1887). O `modulo`
  // de cada rota (usado abaixo em `bloqueado`) é o que a API exige, não o
  // grupo do menu — ver o comentário em `apps/web/src/lib/navegacao.ts`.
  const rotas = rotasDoModulo(chaveModulo);

  const initials = user?.nome
    ? user.nome.split(' ').map((n) => n[0]).slice(0, 2).join('')
    : 'MC';

  const logout = useAuthStore((s) => s.logout);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside className="side" role="navigation" aria-label="Navegação principal">
      {/* Brand & Wordmark */}
      <div className="brand" style={{ cursor: 'pointer' }} onClick={() => navigate('/hub')}>
        <LogoSVG size={30} color={modAtivo?.cor ?? MOD_COLORS.eleg} segment={modAtivo?.seg ?? 0} />
        <div>
          {/*
            O nome vai partido em dois elementos porque o `em` recebe a cor de
            destaque do módulo ativo. Escrito assim, ele não aparece numa busca
            por "Mandato360" no código — foi por isso que este ponto sobreviveu
            à primeira passagem da troca de marca.
          */}
          <b>Eleg<em>360</em></b>
          <small>{modAtivo?.n ?? 'Plataforma Integrada'}</small>
        </div>
      </div>

      {modAtivo && (
        <div className="mod-chip">
          {/*
            A palavra vem de `MODULOS[...].marca`, e não de um `if` com o texto
            escrito aqui. Era assim antes, em dois arquivos diferentes: quando
            a marca mudou, o cartão do Hub acompanhou e este ponto ficou para
            trás, mostrando o nome antigo ao lado do novo na mesma tela.
          */}
          {modAtivo.marca ? (
            <span
              style={{
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
                fontSize: 16,
                letterSpacing: '-.02em',
                color: modAtivo.cor,
              }}
            >
              {modAtivo.marca}
            </span>
          ) : (
            <LogoSVG size={18} color={modAtivo.cor} segment={modAtivo.seg} />
          )}
          <span>{modAtivo.n}</span>
          <span className="sw" onClick={() => navigate('/hub')}>
            ⇄ trocar módulo
          </span>
        </div>
      )}

      <div className="tenant">
        <b>{mandato?.nome_parlamentar || mandato?.nome || 'Gab. Ver. Ana Silva'}</b>
        {/* O período vinha cravado como `Mandato 2025–2028`: acertava por
            coincidência para uma legislatura municipal iniciada em 2025 e
            mentia para qualquer outra. Quando o mandato não informa, some --
            informação ausente é melhor que informação errada. */}
        <span>{[tenantCidadeUf, periodoDoMandato].filter(Boolean).join(' · ')}</span>
      </div>

      {/* Nav — filtrado pelo módulo ativo (rotasDoModulo), cabeçalho de grupo
          inserido sempre que o grupo muda em relação ao item anterior, como
          o protótipo faz em proto:1886. */}
      <nav className="nav">
        {rotas.map((r, i, arr) => {
          const g = grupoDaRota(r.caminho);
          const primeiroDoGrupo = i === 0 || grupoDaRota(arr[i - 1]!.caminho) !== g;
          const badge = contadores[r.caminho];

          // Rota de módulo não contratado: a API recusa (402 ou 404,
          // conforme a política do mandato), então o menu já mostra o
          // bloqueio em vez de deixar o usuário descobrir depois de clicar.
          const bloqueado = !liberado(r.modulo);

          return (
            <Fragment key={r.caminho}>
              {primeiroDoGrupo && (
                <div className="grp">
                  <i aria-hidden="true" />
                  {g}
                </div>
              )}
              <NavLink
                to={r.caminho}
                className={({ isActive }) =>
                  ['nav-link', isActive ? 'active' : '', bloqueado ? 'off' : '']
                    .filter(Boolean)
                    .join(' ')
                }
                aria-disabled={bloqueado}
                title={
                  bloqueado
                    ? `Módulo ${r.modulo?.toUpperCase()} não contratado por este mandato`
                    : undefined
                }
                onClick={(e) => {
                  if (!bloqueado) return;
                  e.preventDefault();
                  toast(
                    `Módulo ${r.modulo?.toUpperCase()} inativo neste mandato. As rotas dele estão bloqueadas na API.`,
                  );
                }}
              >
                <Icone nome={r.icone} />
                <span>{r.nome}</span>
                {!bloqueado && badge !== undefined && badge > 0 && (
                  <span className="cnt">{badge}</span>
                )}
                {r.lock && <span className="lock">{r.lock}</span>}
              </NavLink>
            </Fragment>
          );
        })}
      </nav>

      {/* Me (usuário logado), botão de tema (proto:324) e botão Sair.
          O protótipo move "Sair" para o menu do avatar, mas este shell ainda
          não tem um menu de avatar implementado (fora do escopo da Task 1.7)
          — então "Sair" continua aqui, ao lado do botão de tema. */}
      <div className="me">
        <div className="avatar" aria-hidden="true">{initials}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>
            {user?.nome ?? 'Ana Silva'}
          </div>
          <div style={{ color: 'var(--ink3)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.perfil ?? 'Vereadora · Gabinete'}
          </div>
        </div>
        <button
          className="theme"
          style={{ marginLeft: 'auto' }}
          onClick={toggleTheme}
          title="Tema"
          aria-label="Alternar tema"
          id="sidebar-theme-btn"
        >
          ◐
        </button>
        <button
          className="logout-btn"
          onClick={handleLogout}
          title="Sair do Eleg360"
          aria-label="Sair da conta"
          id="sidebar-logout-btn"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11.5L14.5 8 11 4.5M14 8H6" />
          </svg>
          <span>Sair</span>
        </button>
      </div>
    </aside>
  );
}
